import { useState, type CSSProperties } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { useAuth } from "@/hooks/useAuth";
import FamilyCircleList from "@/components/FamilyCircleList";
import { apiRequest } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { 
  UserPlus, 
  Shield, 
  Users, 
  Eye, 
  Settings, 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle 
} from "lucide-react";

const legacyTheme: CSSProperties = {
  "--background": "220 58% 9%",
  "--foreground": "0 0% 100%",
  "--card": "221 45% 14%",
  "--card-foreground": "0 0% 100%",
  "--muted": "221 35% 18%",
  "--muted-foreground": "215 20% 80%",
  "--border": "40 70% 55%",
  "--input": "221 25% 20%",
  "--ring": "40 70% 55%",
};

const FamilyCircle = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [accessLevelOpen, setAccessLevelOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState<any>(null);
  
  // Form state
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    relationship: "",
    accessLevel: "viewer"
  });
  
  // Get the active athlete (shared across the app; switchable)
  const { athlete } = useActiveAthlete();

  // Get family circle members
  const { data: familyMembers, isLoading } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "family-circle"],
    enabled: !!athlete?.id,
  });
  
  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest(`/athletes/${athlete?.id}/family-circle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || "Failed to add member");
      }

      return response.json().catch(() => ({}));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athlete?.id, "family-circle"] });
      toast({
        title: "Success",
        description: `${newMember.name} Added to The Loop`
      });
      setInviteOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to add to The Loop",
        variant: "destructive"
      });
    }
  });
  
  // Update member access level mutation
  const updateMemberMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest(`/family-circle/${data.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessLevel: data.accessLevel }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || "Failed to update member");
      }

      return response.json().catch(() => ({}));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athlete?.id, "family-circle"] });
      toast({
        title: "Success",
        description: "Access level updated successfully"
      });
      setAccessLevelOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update access level",
        variant: "destructive"
      });
    }
  });
  
  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(`/family-circle/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || "Failed to remove member");
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athlete?.id, "family-circle"] });
      toast({
        title: "Success",
        description: "Member removed successfully"
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive"
      });
    }
  });
  
  const handleInvite = () => {
    const normalizedEmail = newMember.email.trim().toLowerCase();

    if (!newMember.name || !normalizedEmail || !newMember.relationship || !newMember.accessLevel) {
      toast({
        title: "Validation Error",
        description: "Name, email, relationship, and access level are required.",
        variant: "destructive"
      });
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(normalizedEmail)) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    addMemberMutation.mutate({
      name: newMember.name.trim(),
      email: normalizedEmail,
      relationship: newMember.relationship,
      accessLevel: newMember.accessLevel
    });
  };
  
  const handleChangeAccessLevel = () => {
    if (currentMember && currentMember.id) {
      updateMemberMutation.mutate({
        id: currentMember.id,
        accessLevel: currentMember.accessLevel
      });
    }
  };
  
  const handleRemoveMember = (id: string) => {
    if (confirm("Are you sure you want to remove this member from your family circle?")) {
      removeMemberMutation.mutate(id);
    }
  };
  
  const resetForm = () => {
    setNewMember({
      name: "",
      email: "",
      relationship: "",
      accessLevel: "viewer"
    });
  };
  
  const openAccessLevelDialog = (member: any) => {
    setCurrentMember({...member});
    setAccessLevelOpen(true);
  };
  
  const accessLevelInfo = {
    admin: {
      title: "Admin Access",
      description: "Can view all content, invite new members, and manage settings",
      icon: <Shield className="h-5 w-5 text-primary" />
    },
    editor: {
      title: "Editor Access",
      description: "Can view and edit all content, but can't manage family circle",
      icon: <Users className="h-5 w-5 text-secondary" />
    },
    viewer: {
      title: "Viewer Access",
      description: "Can only view content, can't edit or manage anything",
      icon: <Eye className="h-5 w-5 text-accent" />
    },
    limited: {
      title: "Limited Access",
      description: "Can only view selected content that you explicitly share",
      icon: <Clock className="h-5 w-5 text-slate-400" />
    }
  };

  return (
    <div className="container mx-auto min-h-screen px-4 py-6 text-white" style={legacyTheme}>
      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-4 py-4 shadow-lg shadow-black/25 md:flex-row md:items-center md:justify-between">
        <h1 className="font-montserrat font-bold text-2xl md:text-3xl text-white">In the Loop</h1>
        
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467] flex items-center font-semibold">
              <UserPlus className="h-4 w-4 mr-2" /> Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent className="border-[#E0A458]/40 bg-[#111827] text-slate-100 shadow-2xl">
            <DialogHeader>
              <DialogTitle>Add to The Loop</DialogTitle>
              <DialogDescription className="text-slate-300">
                Invite a family member, coach, or friend to view your athletic journey.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 text-slate-100">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input 
                  id="name" 
                  placeholder="e.g., Mom, Coach Williams" 
                  value={newMember.name}
                  onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="Required to link member access" 
                  value={newMember.email}
                  onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="relationship">Relationship</Label>
                <Select 
                  value={newMember.relationship}
                  onValueChange={(value) => setNewMember({...newMember, relationship: value})}
                >
                  <SelectTrigger id="relationship" className="border-[#415A77] bg-[#0B1220] text-slate-100 data-[placeholder]:text-slate-400 focus:ring-[#E0A458]">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="parent">Parent</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="sibling">Sibling</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="grandparent">Grandparent</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="relative">Other Relative</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="coach">Coach</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="friend">Friend</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="access">Access Level</Label>
                <Select 
                  value={newMember.accessLevel}
                  onValueChange={(value) => setNewMember({...newMember, accessLevel: value})}
                >
                  <SelectTrigger id="access" className="border-[#415A77] bg-[#0B1220] text-slate-100 data-[placeholder]:text-slate-400 focus:ring-[#E0A458]">
                    <SelectValue placeholder="Select access level" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="editor">Editor</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="viewer">Viewer</SelectItem>
                    <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="limited">Limited Access</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-300 mt-1">
                  {accessLevelInfo[newMember.accessLevel as keyof typeof accessLevelInfo]?.description}
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" className="border-[#415A77] bg-transparent text-slate-100 hover:bg-[#1B263B] hover:text-white" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button onClick={handleInvite} disabled={addMemberMutation.isPending} className="bg-[#E0A458] text-black hover:bg-[#f0b467]">
                {addMemberMutation.isPending ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="members">
            <TabsList className="mb-4">
              <TabsTrigger value="members">Who's in The Loop</TabsTrigger>
              <TabsTrigger value="pending">Pending Invitations</TabsTrigger>
              <TabsTrigger value="access">Access Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="members">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Who's in The Loop</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="text-center p-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-2 text-slate-300">Loading who's in the loop...</p>
                    </div>
                  ) : familyMembers && familyMembers.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Relationship</TableHead>
                          <TableHead>Access Level</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {familyMembers.map((member: any) => (
                          <TableRow key={member.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold mr-2">
                                  {member.name.charAt(0).toUpperCase()}
                                </div>
                                {member.name}
                              </div>
                            </TableCell>
                            <TableCell>
                              {member.relationship.charAt(0).toUpperCase() + member.relationship.slice(1)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                {accessLevelInfo[member.accessLevel as keyof typeof accessLevelInfo]?.icon}
                                <span className="ml-2 capitalize">{member.accessLevel}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {member.isActive ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0B1220] text-white">
                                  <Clock className="w-3 h-3 mr-1" /> Pending
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openAccessLevelDialog(member)}
                              >
                                <Settings className="h-4 w-4 mr-1" /> 
                                <span className="sr-only md:not-sr-only md:inline-block">
                                  Change Access
                                </span>
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive"
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                <XCircle className="h-4 w-4 mr-1" /> 
                                <span className="sr-only md:not-sr-only md:inline-block">
                                  Remove
                                </span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center p-6">
                      <div className="mb-4 mx-auto rounded-full bg-[#0B1220] p-3 inline-block">
                        <Users className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-100 font-medium">No one in The Loop yet</p>
                      <p className="text-slate-400 text-sm mt-1">
                        Invite family members, coaches, or friends to join The Loop
                      </p>
                      <Button
                        className="mt-4 bg-primary"
                        onClick={() => setInviteOpen(true)}
                      >
                        <UserPlus className="h-4 w-4 mr-2" /> Invite Member
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="pending">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending Invitations</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="text-center p-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-2 text-slate-300">Loading invitations...</p>
                    </div>
                  ) : familyMembers && familyMembers.filter((m: any) => !m.isActive).length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Invited On</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {familyMembers
                          .filter((m: any) => !m.isActive)
                          .map((member: any) => (
                            <TableRow key={member.id}>
                              <TableCell className="font-medium">{member.name}</TableCell>
                              <TableCell>{member.email || "Missing email"}</TableCell>
                              <TableCell>
                                {new Date(member.createdAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-primary"
                                >
                                  <Send className="h-4 w-4 mr-1" /> Resend
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() => handleRemoveMember(member.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" /> Cancel
                                </Button>
                              </TableCell>
                            </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center p-6">
                      <p className="text-slate-100">No pending invitations</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="access">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Content Privacy Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="journal-privacy">Journal Entries</Label>
                        <Select defaultValue="private">
                          <SelectTrigger id="journal-privacy" className="w-[180px]">
                            <SelectValue placeholder="Select privacy" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="private">Private</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="family">Family Only</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="selective">Selective Sharing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-400">
                        Control who can see your journal entries by default
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="media-privacy">Media Library</Label>
                        <Select defaultValue="family">
                          <SelectTrigger id="media-privacy" className="w-[180px]">
                            <SelectValue placeholder="Select privacy" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="private">Private</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="family">Family Only</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="selective">Selective Sharing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-400">
                        Control who can see your photos and videos by default
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="stats-privacy">Stats & Milestones</Label>
                        <Select defaultValue="family">
                          <SelectTrigger id="stats-privacy" className="w-[180px]">
                            <SelectValue placeholder="Select privacy" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="private">Private</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="family">Family Only</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="selective">Selective Sharing</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="public">Public</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-400">
                        Control who can see your athletic achievements and stats
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="recruiting-privacy">Recruiting Information</Label>
                        <Select defaultValue="private">
                          <SelectTrigger id="recruiting-privacy" className="w-[180px]">
                            <SelectValue placeholder="Select privacy" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="private">Private</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="family">Family Only</SelectItem>
                            <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="coaches">Coaches Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-400">
                        Control who can see your recruiting timeline and college interests
                      </p>
                    </div>
                    
                    <Button className="bg-primary w-full">Save Privacy Settings</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          <FamilyCircleList 
            familyMembers={familyMembers || []} 
            athleteId={athlete?.id}
            onAddMember={() => setInviteOpen(true)}
          />
          
          <Card>
            <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
              <CardTitle className="text-lg flex items-center">
                <Shield className="h-5 w-5 mr-2 text-primary" />
                Access Levels Explained
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4">
                {Object.entries(accessLevelInfo).map(([key, { title, description, icon }]) => (
                  <div key={key} className="flex items-start">
                    <div className="mt-1 mr-3">{icon}</div>
                    <div>
                      <h4 className="font-medium">{title}</h4>
                      <p className="text-sm text-slate-300">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Change Access Level Dialog */}
      <Dialog open={accessLevelOpen} onOpenChange={setAccessLevelOpen}>
        <DialogContent className="border-[#E0A458]/40 bg-[#111827] text-slate-100 shadow-2xl">
          <DialogHeader>
            <DialogTitle>Change Access Level</DialogTitle>
            <DialogDescription className="text-slate-300">
              Update what {currentMember?.name} can see and do in your athletic journey.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 text-slate-100">
            <div className="space-y-2">
              <Label htmlFor="member-access">Access Level for {currentMember?.name}</Label>
              <Select 
                value={currentMember?.accessLevel}
                onValueChange={(value) => setCurrentMember({...currentMember, accessLevel: value})}
              >
                <SelectTrigger id="member-access" className="border-[#415A77] bg-[#0B1220] text-slate-100 data-[placeholder]:text-slate-400 focus:ring-[#E0A458]">
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1220] border-[#415A77] text-slate-100">
                  <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="admin">Admin (Full Access)</SelectItem>
                  <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="editor">Editor</SelectItem>
                  <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="viewer">Viewer</SelectItem>
                  <SelectItem className="focus:bg-[#1B263B] focus:text-white" value="limited">Limited Access</SelectItem>
                </SelectContent>
              </Select>
              
              {currentMember && (
                <div className="p-3 rounded-md bg-[#0B1220] mt-3 flex items-start border border-[#415A77]/60">
                  {accessLevelInfo[currentMember.accessLevel as keyof typeof accessLevelInfo]?.icon}
                  <div className="ml-2">
                    <p className="font-medium text-sm">
                      {accessLevelInfo[currentMember.accessLevel as keyof typeof accessLevelInfo]?.title}
                    </p>
                    <p className="text-sm text-slate-300">
                      {accessLevelInfo[currentMember.accessLevel as keyof typeof accessLevelInfo]?.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" className="border-[#415A77] bg-transparent text-slate-100 hover:bg-[#1B263B] hover:text-white" onClick={() => setAccessLevelOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleChangeAccessLevel} 
              disabled={updateMemberMutation.isPending}
              className="bg-[#E0A458] text-black hover:bg-[#f0b467]"
            >
              {updateMemberMutation.isPending ? "Updating..." : "Update Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyCircle;
